import React from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { merge } from 'lodash';

import { Plus, Trash2 } from 'lucide-react';
import Select from '../../components/Select';
import Textarea from '../../components/Textarea';
import { SELECT_STYLES_MODAL } from '../../utils/const';
// import { CURRENT_TEMPLATE_NAME, SELECT_STYLES_MODAL } from '../../utils/const';
import ConfirmationModal from '../../components/ConfirmationModal';
// import SvgIcon from '../../components/SvgIcon';
import {
  sustainabilityNumber,
  selectLabel,
  selectValue,
  sdgCardColorClass,
} from '../../utils/helpers';
import { getColorDependsOnBackground } from '../../utils/helpers/getColorDependsOnBackground';
import Button from '../../components/Button';

const CustomTable = ({
  editMode = false,
  locales: {
    common,
    portfolio: { sustainability },
    sustainability: { sdgs, intro },
  },
  collection: collectionCards,
  // iconFillTable,
  backgroundTable,
  borderCardTableColor,
  backgroundCells,
  // backgroundIcon,
  backgroundRow,
  // iconTableStyles,
  // templateName,
  titleTableColor,
  textColor,
  borderTableColor,
  textStyle,
  isFourthColorPaletteNumber = false,
  addSustainability = () => {},
  buttonStyle,
  buttonVariant = 'outline',
}) => {
  const {
    sustainability: { updateSustainability, removeSustainability },
  } = useDispatch();
  const sustainabilitySdgOptions = sdgs.map((e, index) => ({
    label: e.title,
    value: (index + 1).toString(),
  }));

  // const isCurrentTemplate = templateName === CURRENT_TEMPLATE_NAME;

  const optionStyles = {
    option: (styles) => {
      return {
        ...styles,
        fontFamily: 'inherit',
      };
    },
  };

  const colorDependsOnBackgroundAndPallete = isFourthColorPaletteNumber
    ? getColorDependsOnBackground(backgroundTable)
    : titleTableColor;

  const getBackgroundRowStyle = (i) => {
    return backgroundRow
      ? {
          backgroundColor: i % 2 !== 0 ? backgroundRow : backgroundCells,
          borderColor: borderTableColor,
        }
      : {
          backgroundColor: backgroundCells,
          borderColor: borderTableColor,
        };
  };

  const renderTableHead = () => (
    <thead>
      <tr>
        <th
          className={classNames('custom-font-body', editMode && 'width-20')}
          style={{ color: colorDependsOnBackgroundAndPallete }}
        >
          {sustainability.table.sgd}
        </th>
        <th
          className={classNames('custom-font-body', editMode && 'width-40')}
          style={{ color: colorDependsOnBackgroundAndPallete }}
        >
          {sustainability.table.problem}
        </th>
        <th
          className={classNames('custom-font-body', editMode && 'width-40')}
          style={{ color: colorDependsOnBackgroundAndPallete }}
        >
          {sustainability.table.value}
        </th>
      </tr>
    </thead>
  );

  return (
    <div
      className={classNames(
        'table-wrapper relative',
        !editMode && 'table-wrapper--qa-variant table-wrapper--qa-goal-inline',
      )}
      style={{
        backgroundColor: borderCardTableColor,
        marginTop: !editMode ? 20 : undefined,
      }}
    >
      {/* Legacy SDG table kept for edit-mode; view-mode rendering is intentionally customized below. */}
      <table className="table" style={{ backgroundColor: backgroundTable }}>
        {renderTableHead()}
        <tbody
          className={`${textStyle} custom-font-body`}
          style={{ color: textColor }}
        >
          {collectionCards.map((item, i) =>
            editMode ? (
              <tr key={item.id} style={getBackgroundRowStyle(i)}>
                <td>
                  <Select
                    className={`bmc-form-select ${textStyle} custom-font-body`}
                    styles={merge(SELECT_STYLES_MODAL, optionStyles)}
                    options={sustainabilitySdgOptions}
                    value={selectValue(item.sdg, selectLabel(item.sdg, sdgs))}
                    onChange={({ value }) =>
                      updateSustainability({
                        name: 'sdg',
                        value,
                        id: item.id,
                      })
                    }
                  />
                </td>

                <td>
                  <Textarea
                    value={item.problem}
                    delay={700}
                    className={`bmc-form-textarea ${textStyle} custom-font-body flex`}
                    onChange={(e) =>
                      updateSustainability({
                        name: 'problem',
                        value: e.target.value,
                        id: item.id,
                      })
                    }
                  />
                </td>

                <td>
                  <Textarea
                    value={item.value}
                    delay={700}
                    className={`bmc-form-textarea ${textStyle} custom-font-body flex`}
                    onChange={(e) =>
                      updateSustainability({
                        name: 'value',
                        value: e.target.value,
                        id: item.id,
                      })
                    }
                  />
                </td>

                <td>
                  {collectionCards.length > 1 && (
                    <ConfirmationModal
                      className="ml-3 hover:bg-muted-lighter/60 text-muted-darker"
                      body={common.deleteConfirmation}
                      confirmText="Delete"
                      onConfirm={() => removeSustainability({ id: item.id })}
                      buttonAriaLabel="Delete"
                    >
                      <Trash2 className="w-6 min-w-6 h-auto" />
                    </ConfirmationModal>
                  )}
                </td>
              </tr>
            ) : (
              <tr key={`qa-inline-${item.id}`} style={getBackgroundRowStyle(i)}>
                <td style={{ borderColor: borderTableColor }}>
                  <div className="sustainability-goal-cell sustainability-goal-cell--inline">
                    <div
                      className={classNames(
                        'sustainability-goal-cell__square',
                        sdgCardColorClass(sustainabilityNumber(item.sdg)),
                      )}
                    >
                      {sdgs[sustainabilityNumber(item.sdg) - 1]?.title ||
                        `Goal ${sustainabilityNumber(item.sdg)}`}
                    </div>
                  </div>
                  <div
                    className="border-right"
                    style={{ backgroundColor: borderTableColor }}
                  />
                </td>
                <td style={{ borderColor: borderTableColor }}>
                  <div className="title" style={{ color: titleTableColor }}>
                    {sustainability.table.problem}
                  </div>
                  <div>{item.problem}</div>
                  <div
                    className="border-right"
                    style={{ backgroundColor: borderTableColor }}
                  />
                </td>
                <td style={{ borderColor: borderTableColor }}>
                  <div className="title" style={{ color: titleTableColor }}>
                    {sustainability.table.value}
                  </div>
                  <div>{item.value}</div>
                </td>

                {/* Legacy view-mode cell rendering (kept for future re-enable)
                <td style={{ borderColor: borderTableColor }}>
                  <div className="title" style={{ color: titleTableColor }}>
                    {sustainability.table.sgd}
                  </div>
                  <div
                    className={classNames(
                      'icon',
                      isCurrentTemplate &&
                        sdgCardColorClass(sustainabilityNumber(item.sdg)),
                      iconTableStyles,
                    )}
                    style={{
                      backgroundColor: backgroundIcon,
                    }}
                  >
                    <div className="icon-size" style={{ fill: iconFillTable }}>
                      <SvgIcon name={`sdg${sustainabilityNumber(item.sdg)}`} />
                    </div>
                  </div>
                  <div
                    className="border-right"
                    style={{ backgroundColor: borderTableColor }}
                  />
                </td>
                */}
              </tr>
            ),
          )}
        </tbody>
      </table>

      {editMode && (
        <Button
          variant={buttonVariant}
          onClick={addSustainability}
          className={`w-full bg-transparent ${buttonStyle}`}
        >
          <Plus className="w-7 h-auto" />
          {intro.card.createNewSdg}
        </Button>
      )}
    </div>
  );
};

export default CustomTable;
